/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Link, useNavigate } from 'react-router-dom'

import { Alert, Badge, Button, Checkbox, PageContainer, PageHeader } from '@/components/ui'
import { useApi } from '@/hooks'
import { TOTAL_INSIGHTS } from '@/insights/insightCatalogue'
import logger from '@/utils/logger'

import { AdminHeaderCells, AdminRowCells, type AdminColumns, useAdminTable } from '../adminTable'
import { LoadingPanel } from '../LoadingPanel'
import { APP_GRANTS, APP_GRANT_LABELS, type AdminCustomerSummary } from '../permissions'

const log = logger({ category: 'AdminCompaniesPage' })

// A company that has never been saved caps nothing — its users keep their own
// lists — so its app cells read as "not configured" rather than off.
const hasProfile = (company: AdminCustomerSummary) => company.updatedAt !== null

const COLUMNS: AdminColumns<AdminCustomerSummary> = [
  {
    id: 'name',
    header: 'Name',
    accessorFn: (company) => company.name,
    cell: ({ row }) => (
      <Link className="link link-primary" to={`/admin/companies/${String(row.original.customerId)}`}>
        {row.original.name}
      </Link>
    ),
  },
  { id: 'users', header: 'Users', accessorFn: (company) => company.userCount },
  { id: 'assetGroups', header: 'Asset Groups', accessorFn: (company) => company.assetGroupCount },
  {
    id: 'insights',
    header: 'Insights',
    accessorFn: (company) => company.insights.length,
    meta: { className: 'whitespace-nowrap' },
    cell: ({ row }) => (
      <>
        {`${String(row.original.insights.length)}/${String(TOTAL_INSIGHTS)}`}
        {!hasProfile(row.original) && (
          <Badge size="xs" className="ml-2" title="No profile saved — users keep their own permissions">
            no profile
          </Badge>
        )}
      </>
    ),
  },
  ...APP_GRANTS.map((grant): AdminColumns<AdminCustomerSummary>[number] => ({
    id: grant,
    header: APP_GRANT_LABELS[grant],
    accessorFn: (company) => hasProfile(company) && company.grants.includes(grant),
    meta: {
      tdProps: (company: AdminCustomerSummary) => ({ 'data-testid': `grant-${String(company.customerId)}-${grant}` }),
    },
    cell: ({ row }) =>
      hasProfile(row.original) && row.original.grants.includes(grant) ? (
        <Badge variant="success" size="xs">
          On
        </Badge>
      ) : hasProfile(row.original) ? (
        <span className="text-base-content/40" aria-label="disabled">
          —
        </span>
      ) : (
        <span className="text-base-content/40" aria-label="not configured" title="No profile saved">
          —
        </span>
      ),
  })),
]

const AdminCompaniesPage: React.FC = () => {
  const { api } = useApi()
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<AdminCustomerSummary[]>([])
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState('')
  // Bulk permissions: the checkbox column only appears once the admin asks for it, and the
  // selection is thrown away on leaving so a stale pick can't ride into a later session.
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(() => new Set())

  const load = useCallback(async () => {
    if (!api) return
    setIsLoading(true)
    try {
      const response = await api.get<AdminCustomerSummary[]>({
        path: '/app/admin/customers',
        origin: 'AdminCompaniesPage#load',
      })
      if (response.error) {
        setError(response.message || 'Could not load companies')
      } else {
        setError('')
        setCompanies(response.data ?? [])
      }
    } catch (ex) {
      log.error(ex)
      setError('Could not load companies')
    } finally {
      setIsLoading(false)
    }
  }, [api])

  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return companies
    return companies.filter((c) => c.name.toLowerCase().includes(q) || String(c.customerId).includes(q))
  }, [companies, filter])

  const table = useAdminTable(visible, COLUMNS, { initialSorting: [{ id: 'name', desc: false }] })

  const visibleIds = useMemo(() => visible.map((c) => c.customerId), [visible])
  const visibleSelectedCount = visibleIds.filter((id) => selected.has(id)).length
  const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length

  const toggleSelectMode = () => {
    setSelectMode((on) => !on)
    setSelected(new Set())
  }

  const toggleOne = (customerId: number, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(customerId)
      else next.delete(customerId)
      return next
    })
  }

  // Only rows the admin can see — never companies hidden by the filter.
  const toggleMany = (ids: number[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (checked) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  const columnCount = (selectMode ? 5 : 4) + APP_GRANTS.length

  return (
    <PageContainer>
      <Link to="/admin" className="link link-hover mb-2 inline-block text-sm">
        ← Admin
      </Link>
      <PageHeader
        title="Companies"
        subtitle={isLoading ? undefined : `${companies.length} ${companies.length === 1 ? 'company' : 'companies'}`}
      />

      {error && <Alert message={error} type="error" />}

      <div className="mb-3 flex items-end gap-3">
        <input
          className="input input-bordered input-sm w-full max-w-md"
          placeholder="Filter by name or id"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter companies"
        />
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            className={`btn btn-sm ${selectMode ? 'btn-active' : 'btn-outline'}`}
            onClick={toggleSelectMode}
            aria-pressed={selectMode}
          >
            {selectMode ? 'Done selecting' : 'Bulk Update Permissions'}
          </button>
        </div>
      </div>

      {selectMode && (
        <div
          className="mb-2 flex flex-wrap items-center gap-3 rounded-lg bg-base-200 px-3 py-2"
          data-testid="selection-bar"
        >
          <span className="text-sm">{selected.size} selected</span>
          <Button
            variation="primary"
            size="sm"
            isDisabled={selected.size === 0}
            handleClick={() => navigate('/admin/companies/permissions', { state: { customerIds: [...selected] } })}
          >
            Set Permissions
          </Button>
          {!allVisibleSelected && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleMany(visibleIds, true)}>
              Select all {visible.length} matching
            </button>
          )}
          {selected.size > 0 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <LoadingPanel label="Loading companies…" />
      ) : (
        <div className="overflow-x-auto rounded-lg bg-base-100 shadow">
          <table className="table table-sm table-zebra">
            <thead>
              <tr>
                {selectMode && (
                  <th className="w-10">
                    <Checkbox
                      id="select-all-companies"
                      label={<span className="sr-only">Select all matching</span>}
                      checked={allVisibleSelected}
                      indeterminate={visibleSelectedCount > 0 && !allVisibleSelected}
                      onChange={(checked) => toggleMany(visibleIds, checked)}
                      data-testid="select-all"
                    />
                  </th>
                )}
                <AdminHeaderCells table={table} />
              </tr>
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => {
                const company = row.original
                return (
                  <tr key={company.customerId}>
                    {selectMode && (
                      <td>
                        <Checkbox
                          id={`select-${String(company.customerId)}`}
                          label={<span className="sr-only">Select {company.name}</span>}
                          checked={selected.has(company.customerId)}
                          onChange={(checked) => toggleOne(company.customerId, checked)}
                          data-testid={`select-${String(company.customerId)}`}
                        />
                      </td>
                    )}
                    <AdminRowCells row={row} />
                  </tr>
                )
              })}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="p-4 text-center italic text-base-content/60">
                    No companies match that filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </PageContainer>
  )
}

export default AdminCompaniesPage
